import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { isLeavingClick } from "../settingsLayout/unsavedChanges";

const LeaveGuardContext = createContext({
  register: () => {},
  unregister: () => {},
  guardedNavigate: null,
});

/**
 * Asks before leaving a screen with unsaved work: in-app link clicks, code
 * navigation through `useGuardedNavigate`, and closing or reloading the tab.
 *
 * BrowserRouter has no navigation blocker, so link clicks are caught before
 * React Router's own handler. The browser Back button is not covered until the
 * app moves to a data router with `useBlocker`.
 */
export const LeaveGuardProvider = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentPath = useRef(pathname);
  currentPath.current = pathname;
  const guards = useRef(new Map());
  const bypass = useRef(false);
  const [hasActiveGuard, setHasActiveGuard] = useState(false);
  const [pending, setPending] = useState(null);

  const activeGuard = useCallback(() => {
    if (bypass.current) return null;
    return [...guards.current.values()].find(guard => guard.when) || null;
  }, []);

  const refresh = useCallback(() => setHasActiveGuard(Boolean(activeGuard())), [activeGuard]);

  const register = useCallback(
    (id, guard) => {
      guards.current.set(id, guard);
      refresh();
    },
    [refresh],
  );

  const unregister = useCallback(
    id => {
      guards.current.delete(id);
      refresh();
    },
    [refresh],
  );

  const guardedNavigate = useCallback(
    (to, options) => {
      const guard = activeGuard();
      if (guard && typeof to === "string") {
        setPending({ to, options, guard });
        return;
      }
      navigate(to, options);
    },
    [activeGuard, navigate],
  );

  useEffect(() => {
    if (!hasActiveGuard) return undefined;
    const warn = event => {
      if (!activeGuard()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const intercept = event => {
      const guard = activeGuard();
      if (!guard) return;
      const anchor = event.target.closest?.("a[href]");
      if (!isLeavingClick(event, anchor, currentPath.current)) return;
      event.preventDefault();
      event.stopPropagation();
      const url = new URL(anchor.href, window.location.href);
      setPending({ to: `${url.pathname}${url.search}${url.hash}`, guard });
    };
    window.addEventListener("beforeunload", warn);
    document.addEventListener("click", intercept, true);
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.removeEventListener("click", intercept, true);
    };
  }, [hasActiveGuard, activeGuard]);

  const leave = () => {
    const { to, options, guard } = pending;
    setPending(null);
    guard.onDiscard?.();
    bypass.current = true;
    navigate(to, options);
    window.setTimeout(() => {
      bypass.current = false;
      refresh();
    }, 0);
  };

  const value = useMemo(
    () => ({ register, unregister, guardedNavigate }),
    [register, unregister, guardedNavigate],
  );

  return (
    <LeaveGuardContext.Provider value={value}>
      {children}
      {pending ? (
        <ConfirmationModal
          titleOfModal={pending.guard.title}
          description={pending.guard.description}
          btnName="Discard and leave"
          btnVariant="danger"
          closeModal={() => setPending(null)}
          handleModalWork={leave}
        />
      ) : null}
    </LeaveGuardContext.Provider>
  );
};

/**
 * Registers a screen's unsaved state. While `when` is true, leaving asks first.
 * `onDiscard` runs when the person chooses Discard and leave.
 *
 * @param {{when: boolean, title?: string, description?: string, onDiscard?: function()}} args
 */
export const useLeaveGuard = ({
  when,
  title = "Discard unsaved changes?",
  description = "You have changes that haven't been saved. Leave this page and discard them?",
  onDiscard,
}) => {
  const { register, unregister } = useContext(LeaveGuardContext);
  const id = useId();

  useEffect(() => {
    register(id, { when: Boolean(when), title, description, onDiscard });
  }, [register, id, when, title, description, onDiscard]);

  useEffect(() => () => unregister(id), [unregister, id]);
};

/**
 * `navigate` that asks first when a screen has unsaved work. Falls back to plain
 * navigation outside the provider.
 *
 * @return {function(string, Object=)}
 */
export const useGuardedNavigate = () => {
  const navigate = useNavigate();
  const { guardedNavigate } = useContext(LeaveGuardContext);
  return guardedNavigate || navigate;
};

/**
 * Tracks whether the user has actually touched a form, so screens that fill in
 * defaults on open (currency, dates) don't ask before leaving untouched.
 *
 * @return {[boolean, !Object]} Flag, and props to spread on the form element.
 */
export const useFormInteraction = () => {
  const [interacted, setInteracted] = useState(false);
  const mark = useCallback(event => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setInteracted(true);
  }, []);
  const props = useMemo(
    () => ({
      onInputCapture: mark,
      onChangeCapture: mark,
      onKeyDownCapture: mark,
      onMouseDownCapture: mark,
    }),
    [mark],
  );
  return [interacted, props];
};
